import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Card, Input, Modal, Button, ActionBar } from '../ui';
import { PageHeader } from '../components/PageHeader';
import { useBookings } from '../context/BookingContext';
import { completeLawyerBooking } from '../services/bookingService';

export const BookingManagementPage = ({ onNavigate, navState }) => {
  const { getFilteredBookings, loading, error, refreshBookings, respondToBooking } = useBookings();
  const [selectedStatus, setSelectedStatus] = useState(navState?.filter || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingBookingId, setUpdatingBookingId] = useState('');
  const [actionError, setActionError] = useState('');
  const [completeTarget, setCompleteTarget] = useState(null);

  const getUrgencyClass = (urgency) => {
    const level = String(urgency || '').toLowerCase();
    if (level === 'urgent') return 'bg-red-50 text-red-700 border border-red-200';
    if (level === 'moderate') return 'bg-amber-50 text-amber-700 border border-amber-200';
    if (level === 'flexible') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    return 'bg-slate-100 text-slate-700 border border-slate-200';
  };

  const buildCaseDataFromBooking = (booking) => ({
    id:        booking.caseId || booking.id,
    bookingId: booking.caseId || booking.id,   // explicit for AICaseIntelligencePage
    caseId:    booking.caseId || booking.id,
    name:      booking.clientName || 'Client',
    type:      booking.caseType   || 'Legal Consultation',
    stage:     booking.status     || 'pending',
    status:    booking.status     || 'pending',
    next:      booking.date       || 'TBD',
    priority:  booking.urgency    || 'flexible',
    location:  booking.location   || '',
    summary:   booking.description || 'No case summary available yet.',
  });

  const openCaseIntelligence = (booking) => {
    onNavigate('cases', {
      caseData: buildCaseDataFromBooking(booking),
    });
  };

  // Filter bookings
  const filteredBookings = getFilteredBookings(selectedStatus).filter((booking) =>
    String(booking.clientName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(booking.caseType || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusTabs = ['all', 'pending', 'accepted', 'completed', 'rejected', 'cancelled'];

  const handleBookingDecision = async (bookingId, decision) => {
    setUpdatingBookingId(String(bookingId));
    setActionError('');
    try {
      await respondToBooking(bookingId, decision);
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.message || 'Failed to update booking status');
    } finally {
      setUpdatingBookingId('');
    }
  };

  const handleCompleteBooking = async () => {
    if (!completeTarget?.id) return;

    setUpdatingBookingId(String(completeTarget.id));
    setActionError('');
    try {
      await completeLawyerBooking(completeTarget.id);
      setCompleteTarget(null);
      await refreshBookings();
      window.dispatchEvent(new CustomEvent('lawyer:booking-updated'));
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.message || 'Failed to mark booking as completed');
    } finally {
      setUpdatingBookingId('');
    }
  };

  return (
    <div>
      <PageHeader
        title="Bookings"
        subtitle="Manage all consultation requests"
        showBack={false}
      />

      <div>
        <Card className="mb-5 p-0 overflow-hidden">
          <div className="p-3">
            <div className="flex gap-2 overflow-x-auto pb-1 px-1">
              {statusTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedStatus(tab)}
                  style={{ minHeight: '40px' }}
                  className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold capitalize whitespace-nowrap shrink-0 transition-colors ${
                    selectedStatus === tab
                      ? 'bg-[#071b33] text-white'
                      : 'bg-transparent text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <Card className="mb-6 p-0">
          <div className="p-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-3 text-primary-400" />
              <Input
                placeholder="Search by client name or case type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </Card>

        <div className="mb-4 flex justify-end">
          <button
            onClick={refreshBookings}
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>

        {loading && (
          <Card className="text-center py-10 mb-4">
            <p className="text-slate-500">Loading bookings...</p>
          </Card>
        )}

        {error && (
          <Card className="text-center py-6 mb-4">
            <p className="text-red-600">{error}</p>
          </Card>
        )}

        {actionError && (
          <Card className="text-center py-4 mb-4">
            <p className="text-red-600">{actionError}</p>
          </Card>
        )}

        {!loading && filteredBookings.length === 0 ? (
          <Card className="text-center py-16">
            <p className="text-slate-500">No bookings found for this filter.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => {
              const bookingStatus = String(booking.status || '').toLowerCase();
              return (
              <Card key={booking.id} className="p-0">
                <div className="p-6">
                  {bookingStatus === 'pending' && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                      Waiting for your approval
                    </div>
                  )}
                  {bookingStatus === 'rejected' && (
                    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                      Request rejected
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2.5 sm:gap-3 mb-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 truncate">{booking.clientName}</h3>
                      <p className="text-slate-500 text-xs sm:text-sm mt-0.5 break-words">{booking.caseType} • {booking.location} • {booking.language}</p>
                      <p className="text-xs text-slate-400 mt-1">{booking.date || ""} {booking.time || ""}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getUrgencyClass(booking.urgency)}`}>
                        {booking.urgency}
                      </span>
                    </div>
                  </div>

                  <p className="text-slate-600 bg-slate-50 rounded-xl px-4 py-3 mb-4 text-xs sm:text-sm break-words">"{booking.description}"</p>

                  <div className="pt-2">
                    {bookingStatus === 'pending' && (
                      <ActionBar>
                        <button
                          onClick={() => onNavigate('ai-help')}
                          className="bg-blue-600 text-white font-semibold hover:bg-blue-700 active:bg-blue-800"
                        >
                          AI Intelligence
                        </button>
                        <button
                          onClick={() => openCaseIntelligence(booking)}
                          className="bg-blue-600 text-white font-semibold hover:bg-blue-700 active:bg-blue-800"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleBookingDecision(booking.id, 'accepted')}
                          disabled={updatingBookingId === String(booking.id)}
                          className="bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60"
                        >
                          {updatingBookingId === String(booking.id) ? 'Updating...' : 'Accept'}
                        </button>
                        <button
                          onClick={() => handleBookingDecision(booking.id, 'rejected')}
                          disabled={updatingBookingId === String(booking.id)}
                          className="bg-red-600 text-white font-semibold hover:bg-red-700 active:bg-red-800 disabled:opacity-60"
                        >
                          {updatingBookingId === String(booking.id) ? 'Updating...' : 'Reject'}
                        </button>
                      </ActionBar>
                    )}

                    {bookingStatus === 'accepted' && (
                      <ActionBar>
                        <a
                          href={`tel:${booking?.raw?.client_phone || ''}`}
                          className="bg-amber-600 text-white font-semibold hover:bg-amber-700 active:bg-amber-800"
                        >
                          Call
                        </a>
                        <button
                          onClick={() => {
                            onNavigate('chat', {
                              bookingId: booking.caseId,
                              clientName: booking.clientName,
                            });
                          }}
                          className="bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:bg-emerald-800"
                        >
                          Chat
                        </button>
                        <button
                          onClick={() => {
                            onNavigate('cases', {
                              caseData: buildCaseDataFromBooking(booking),
                            });
                          }}
                          className="bg-indigo-600 text-white font-semibold hover:bg-indigo-700 active:bg-indigo-800"
                        >
                          Docs
                        </button>
                        <button
                          onClick={() => setCompleteTarget(booking)}
                          disabled={updatingBookingId === String(booking.id)}
                          className="bg-slate-900 text-white font-semibold hover:bg-slate-800 active:bg-black disabled:opacity-60"
                        >
                          {updatingBookingId === String(booking.id) ? 'Updating...' : 'Mark as Completed'}
                        </button>
                      </ActionBar>
                    )}

                    {(bookingStatus === 'completed' || bookingStatus === 'rejected' || bookingStatus === 'cancelled') && (
                      <ActionBar>
                        <button
                          onClick={() => openCaseIntelligence(booking)}
                          className="bg-blue-600 text-white font-semibold hover:bg-blue-700 active:bg-blue-800"
                        >
                          View Details
                        </button>
                      </ActionBar>
                    )}
                  </div>
                </div>
              </Card>
            )})}
          </div>
        )}
      </div>

      <Modal
        isOpen={Boolean(completeTarget)}
        onClose={() => setCompleteTarget(null)}
        title="Complete Consultation"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Mark this accepted consultation with {completeTarget?.clientName || 'the client'} as completed? This will move the booking into earned revenue and transaction history.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCompleteTarget(null)} disabled={Boolean(updatingBookingId)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteBooking} disabled={Boolean(updatingBookingId)}>
              {updatingBookingId ? 'Completing...' : 'Mark as Completed'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
